/**
 * Twitter Event Tracking Utility
 * Reusable functions for tracking Twitter conversion events across the site
 */

/**
 * Track a Twitter conversion event
 * @param {string} formName - The name/type of the form (e.g., 'Contact Form', 'Home Assessment', 'Business Assessment')
 * @param {string} status - The status of the submission ('Subscribe' or 'Assessment Submitted')
 * @param {number|null} value - Optional value for the conversion (can be null)
 * @param {Object} data - Event identifiers and custom parameters
 */
export function trackTwitterEvent(formName, status, value = null, data = {}) {
  // Check if we're in the browser and Twitter pixel is loaded
  if (typeof window !== "undefined" && window.twq) {
    try {
      window.twq("event", "tw-qgi6h-qgi6k", {
        value: value,
        status: status,
        conversion_id: data.lead_id || formName,
        form_type: data.form_type || formName,
      });
      console.log("Twitter event tracked:", { formName, status, value });
    } catch (error) {
      console.error("Error tracking Twitter event:", error);
    }
  } else {
    console.warn("Twitter pixel not loaded, event not tracked");
  }
}

/**
 * Predefined event tracking functions for common form types
 */
export const TwitterEvents = {
  /**
   * Track contact form submission
   */
  trackContactForm: (data = {}) => {
    trackTwitterEvent("ContactForm", "completed", null, data);
  },

  /**
   * Track home assessment form submission
   */
  trackHomeAssessment: (data = {}) => {
    trackTwitterEvent("HomeAssessment", "completed", null, data);
  },

  /**
   * Track business assessment form submission
   */
  trackBusinessAssessment: (data = {}) => {
    trackTwitterEvent("BusinessAssessment", "completed", null, data);
  },

  /**
   * Track residential assessment form submission
   */
  trackResidentialAssessment: (data = {}) => {
    trackTwitterEvent("ResidentialAssessment", "completed", null, data);
  },

  /**
   * Track newsletter subscription
   */
  trackNewsletterSubscription: (data = {}) => {
    trackTwitterEvent("Newsletter", "completed", null, data);
  },

  /**
   * Track custom form submission
   * @param {string} formName - Name of the form
   * @param {string} status - Status ('Subscribe' or 'Assessment Submitted')
   * @param {number|null} value - Optional value
   */
  trackCustomForm: (formName, status, value = null, data = {}) => {
    trackTwitterEvent(formName, status, value, data);
  },
};
